import { ReactNode, useEffect, useState, useCallback } from 'react';

import { postMessageToMobileApp } from 'components/app/helpers';
import { countryCode as countryCodeMap } from 'components/Auth/Signup/constants';
import Render from 'components/render';
import { POST_MESSAGE_TO_MOBILE_EVENTS } from 'constants/enums';
import { ENABLE_AUTO_MOBILE_OTP_VERIFICATION } from 'constants/featureFlag';
import { useAutoMobileOTPVerification } from 'hooks/useAutoMobileOTPVerification';
import { userSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import MaterialDialog from 'UIKit/MaterialDialog';

import PhoneOtp from './ConfirmationScreen/PhoneOtp';
import DefaultSuccessContent from './DefaultSuccessContent/DefaultSuccessContent';
import EnterPhone from './EnterPhone/EnterPhone';
import ErrorContent from './ErrorContent';
import styles from './phoneVerification.module.scss';
import { VerificationScreens } from './PhoneVerification.types';

interface PhoneVerificationProps {
  closeModal: () => void;
  requireRedirectToIDVerificationLink?: boolean;
  successScreen?: ReactNode;
  errorContent?: ({ retryClick }: { retryClick: () => void }) => JSX.Element;
  onGetOtpResponse?: (success: boolean, error: unknown) => void;
  showNumber?: boolean;
}

const PhoneVerification = ({
  closeModal,
  requireRedirectToIDVerificationLink = true,
  successScreen,
  errorContent,
  onGetOtpResponse,
  showNumber = false,
}: PhoneVerificationProps) => {
  const [phone, setPhone] = useState('');
  const [screen, setScreen] = useState<VerificationScreens>('addPhone');
  const { country } = useAppSelector(userSelector);
  const { resetData } = useAutoMobileOTPVerification();

  useEffect(() => {
    if (ENABLE_AUTO_MOBILE_OTP_VERIFICATION) {
      postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.GET_HASH_CODE);
    }

    return () => resetData();
  }, [resetData]);

  const handleRetryClick = useCallback(() => {
    setScreen('addPhone');
    setPhone('');
  }, []);

  const [countryCode, setCountryCode] = useState(country ? countryCodeMap[country] : '');

  useEffect(() => {
    if (screen === 'successScreen' && !requireRedirectToIDVerificationLink) {
      closeModal();
    }
  }, [screen, requireRedirectToIDVerificationLink, closeModal]);

  const splittedCountryCode = countryCode.split('+')[1];

  const formattedCode = splittedCountryCode.includes('-')
    ? splittedCountryCode.replace('-', '')
    : splittedCountryCode.replace(' ', '');

  return (
    <MaterialDialog onClose={closeModal} open contentClassName={styles.modalContent}>
      <Render when={screen === 'addPhone'}>
        <EnterPhone
          closeModal={closeModal}
          phone={phone}
          onPhoneChange={setPhone}
          onCountryCodeChange={setCountryCode}
          countryCode={countryCode}
          setScreen={setScreen}
          onGetOtpResponse={onGetOtpResponse}
          formattedCountryCode={formattedCode}
        />
      </Render>
      <Render when={screen === 'confirmOtp'}>
        <PhoneOtp
          closeModal={closeModal}
          countryCode={formattedCode}
          phone={phone}
          country={country}
          setScreen={setScreen}
          showNumber={showNumber}
        />
      </Render>
      <Render when={screen === 'errorScreen'}>
        {errorContent?.({ retryClick: handleRetryClick }) || (
          <ErrorContent closeModal={closeModal} retryClick={handleRetryClick} />
        )}
      </Render>
      <Render when={screen === 'successScreen'}>
        {successScreen || <DefaultSuccessContent closeModal={closeModal} />}
      </Render>
    </MaterialDialog>
  );
};

export default PhoneVerification;
